import { useState, useMemo } from "react";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Calculator, IndianRupee, TrendingUp, Wallet, Send } from "lucide-react";
import { toast } from "sonner";
import { submitFinanceApplication } from "@/lib/api";
import { VEHICLES } from "@/lib/data";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";

const format = (n) => new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(Math.round(n));

export default function EMICalculator() {
  const [price, setPrice] = useState(900000);
  const [down, setDown] = useState(150000);
  const [tenure, setTenure] = useState(60);
  const [rate, setRate] = useState(11);
  const [vehicle, setVehicle] = useState(VEHICLES[0].name);
  const [contact, setContact] = useState({ name: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);

  const { loan, emi, totalInterest, totalPayable } = useMemo(() => {
    const loanAmt = Math.max(0, price - down);
    const r = rate / 12 / 100;
    const n = tenure;
    const emiVal = r === 0 ? loanAmt / n : (loanAmt * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    const total = emiVal * n;
    return {
      loan: loanAmt,
      emi: isFinite(emiVal) ? emiVal : 0,
      totalInterest: total - loanAmt,
      totalPayable: total,
    };
  }, [price, down, tenure, rate]);

  const handleApply = async (e) => {
    e.preventDefault();
    if (!contact.name || !contact.phone) {
      toast.error("Please enter your name and phone");
      return;
    }
    setSubmitting(true);
    try {
      await submitFinanceApplication({
        name: contact.name,
        phone: contact.phone,
        vehicle_interest: vehicle,
        on_road_price: price,
        down_payment: down,
        tenure_months: tenure,
      });
      toast.success("Application received! Our finance team will call you.");
      setContact({ name: "", phone: "" });
    } catch {
      toast.error("Could not submit. Please WhatsApp us to continue.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-testid="emi-calculator">
      {/* Inputs */}
      <div className="glass rounded-3xl p-8">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-11 h-11 rounded-xl bg-red-600/20 border border-red-500/40 flex items-center justify-center">
            <Calculator className="w-5 h-5 text-red-400" />
          </div>
          <div>
            <h3 className="font-display font-bold text-xl">EMI Calculator</h3>
            <p className="text-xs text-slate-400 uppercase tracking-wider">Real-time estimate</p>
          </div>
        </div>

        <div className="space-y-7">
          <SliderField
            testid="emi-price"
            label="On-Road Vehicle Price"
            value={price}
            onChange={setPrice}
            min={500000}
            max={2000000}
            step={10000}
            format={format}
            prefix="₹"
          />
          <SliderField
            testid="emi-down"
            label="Down Payment"
            value={down}
            onChange={(v) => setDown(Math.min(v, price - 50000))}
            min={50000}
            max={Math.max(50000, price - 50000)}
            step={5000}
            format={format}
            prefix="₹"
          />
          <SliderField
            testid="emi-tenure"
            label="Tenure"
            value={tenure}
            onChange={setTenure}
            min={12}
            max={84}
            step={6}
            format={(v) => `${v} months`}
          />
          <SliderField
            testid="emi-rate"
            label="Interest Rate"
            value={rate}
            onChange={setRate}
            min={9}
            max={16}
            step={0.25}
            format={(v) => `${v.toFixed(2)} %`}
          />
        </div>
      </div>

      {/* Output + Apply */}
      <div className="space-y-6">
        <div className="glass rounded-3xl p-8 relative overflow-hidden">
          <div className="absolute -top-24 -right-24 w-64 h-64 radial-glow-red pointer-events-none" />
          <div className="relative">
            <p className="text-xs uppercase tracking-[0.24em] text-slate-400 font-mono-brand">Your Estimated Monthly EMI</p>
            <div className="mt-4 flex items-baseline gap-2">
              <IndianRupee className="w-8 h-8 text-red-500" />
              <span
                className="font-display text-5xl md:text-6xl font-black text-white tracking-tighter"
                data-testid="emi-result"
              >
                {format(emi)}
              </span>
              <span className="text-slate-400 text-sm">/ month</span>
            </div>

            <div className="grid grid-cols-3 gap-3 mt-8">
              <MiniStat icon={Wallet} label="Loan Amt" value={`₹${format(loan)}`} />
              <MiniStat icon={TrendingUp} label="Interest" value={`₹${format(totalInterest)}`} />
              <MiniStat icon={IndianRupee} label="Total" value={`₹${format(totalPayable)}`} />
            </div>
          </div>
        </div>

        {/* Apply */}
        <Card className="bg-[#0A1229] border-white/10">
          <CardContent className="p-6">
            <h4 className="font-display font-bold text-lg mb-1">Apply for Finance</h4>
            <p className="text-sm text-slate-400 mb-5">Our finance team will call you back within business hours.</p>
            <form onSubmit={handleApply} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-300">Name</Label>
                  <Input
                    value={contact.name}
                    onChange={(e) => setContact({ ...contact, name: e.target.value })}
                    placeholder="Full name"
                    className="mt-1.5 bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-red-500"
                    data-testid="emi-apply-name"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-300">Phone</Label>
                  <Input
                    value={contact.phone}
                    onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                    type="tel"
                    placeholder="10-digit mobile"
                    className="mt-1.5 bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-red-500"
                    data-testid="emi-apply-phone"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-300">Vehicle</Label>
                <Select value={vehicle} onValueChange={setVehicle}>
                  <SelectTrigger
                    className="mt-1.5 bg-white/5 border-white/10 text-white"
                    data-testid="emi-apply-vehicle"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A1229] text-white border-white/10">
                    {VEHICLES.map((v) => (
                      <SelectItem key={v.slug} value={v.name}>
                        {v.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold rounded-full"
                data-testid="emi-apply-submit"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Submit Finance Application
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function SliderField({ label, value, onChange, min, max, step, format, prefix, testid }) {
  return (
    <div data-testid={`${testid}-field`}>
      <div className="flex items-center justify-between mb-3">
        <Label className="text-sm text-slate-300 uppercase tracking-wider">{label}</Label>
        <span className="font-display font-bold text-lg text-white">
          {prefix && <span className="text-slate-500 mr-1">{prefix}</span>}
          {format(value)}
        </span>
      </div>
      <Slider
        min={min}
        max={max}
        step={step}
        value={[value]}
        onValueChange={([v]) => onChange(v)}
        className="[&_[role=slider]]:bg-red-500 [&_[role=slider]]:border-red-500"
        data-testid={`${testid}-slider`}
      />
      <div className="flex justify-between text-[10px] text-slate-500 mt-1.5 font-mono-brand">
        <span>{prefix}{format(min)}</span>
        <span>{prefix}{format(max)}</span>
      </div>
    </div>
  );
}

function MiniStat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl bg-white/5 border border-white/5 px-3 py-3">
      <div className="flex items-center gap-1.5 text-slate-400 mb-1">
        <Icon className="w-3 h-3" />
        <span className="text-[10px] uppercase tracking-wider">{label}</span>
      </div>
      <div className="text-sm font-semibold text-white truncate">{value}</div>
    </div>
  );
}
